import React from 'react';
import { Box, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography } from '@mui/material';
import { EmojiEvents } from '@mui/icons-material';
import { TableSurface, headerCellSx, bodyRowSx, TableEmptyState } from '../../common/pageKit';
import { formatINR, formatNumber, formatPct } from '../../../dashboard/primitives';
import { RepPerformance } from '../../../types/salesAnalytics';

interface RepLeaderboardProps {
  reps: RepPerformance[] | null;
  loading: boolean;
}

const numCellSx = { fontVariantNumeric: 'tabular-nums' as const, textAlign: 'right' as const };

/**
 * Ranked by won value (the order the API already returns). A rep with no
 * `sales_targets` row for the period shows quota/attainment as an em dash —
 * a missing quota is not a 0% quota, and `attainmentPct` is only 0% when a
 * quota exists and nothing was sold against it.
 */
const RepLeaderboard: React.FC<RepLeaderboardProps> = ({ reps, loading }) => (
  <Box sx={{ mb: 3 }}>
    <Typography sx={{ fontWeight: 700, mb: 1.5 }}>Rep Leaderboard</Typography>
    <TableSurface>
      <Box sx={{ overflowX: 'auto' }}>
        <Table size="small" sx={{ minWidth: 920 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={headerCellSx}>Rep</TableCell>
              <TableCell sx={headerCellSx} align="right">Won</TableCell>
              <TableCell sx={headerCellSx} align="right">Won Value</TableCell>
              <TableCell sx={headerCellSx} align="right">Lost</TableCell>
              <TableCell sx={headerCellSx} align="right">Win Rate</TableCell>
              <TableCell sx={headerCellSx} align="right">Avg Deal Size</TableCell>
              <TableCell sx={headerCellSx} align="right">Open Pipeline</TableCell>
              <TableCell sx={headerCellSx} align="right">Quota</TableCell>
              <TableCell sx={headerCellSx} align="right">Attainment</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? null : !reps || reps.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} sx={{ border: 0 }}>
                  <TableEmptyState title="No rep activity in this period" hint="Opportunities need an assigned rep to appear here." />
                </TableCell>
              </TableRow>
            ) : (
              reps.map((rep, i) => (
                <TableRow key={rep.userId} sx={bodyRowSx}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {i === 0 && rep.wonValue > 0 && <EmojiEvents sx={{ fontSize: 16, color: '#f59e0b' }} />}
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {rep.fullName ?? `User #${rep.userId}`}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell sx={numCellSx}>{formatNumber(rep.wonCount)}</TableCell>
                  <TableCell sx={numCellSx}>{formatINR(rep.wonValue)}</TableCell>
                  <TableCell sx={numCellSx}>{formatNumber(rep.lostCount)}</TableCell>
                  <TableCell sx={numCellSx}>
                    {rep.winRatePct === null ? (
                      <Tooltip title="No deals closed yet"><span>—</span></Tooltip>
                    ) : (
                      formatPct(rep.winRatePct)
                    )}
                  </TableCell>
                  <TableCell sx={numCellSx}>
                    {rep.avgDealSize === null ? (
                      <Tooltip title="No deals won yet"><span>—</span></Tooltip>
                    ) : (
                      formatINR(rep.avgDealSize)
                    )}
                  </TableCell>
                  <TableCell sx={numCellSx}>
                    {formatNumber(rep.openPipelineCount)}
                    <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                      ({formatINR(rep.openPipelineValue, true)})
                    </Typography>
                  </TableCell>
                  <TableCell sx={numCellSx}>
                    {rep.quota === null ? (
                      <Tooltip title="No quota set for this rep in this period"><span>—</span></Tooltip>
                    ) : (
                      formatINR(rep.quota, true)
                    )}
                  </TableCell>
                  <TableCell sx={numCellSx}>
                    {rep.attainmentPct === null ? (
                      <Tooltip title="No quota set for this rep in this period"><span>—</span></Tooltip>
                    ) : (
                      <Typography
                        component="span"
                        sx={{
                          fontWeight: 700,
                          fontVariantNumeric: 'tabular-nums',
                          color: rep.attainmentPct >= 100 ? 'success.main' : 'text.primary',
                        }}
                      >
                        {formatPct(rep.attainmentPct)}
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Box>
    </TableSurface>
  </Box>
);

export default RepLeaderboard;
